import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { VisiousSettings, WatchLaterChoice } from '@visious/core';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';
import { useSettingsSlice, writeSettings } from '../platform/settings';
import { useEntryMutations } from './mutations';
import { watchLaterQueryKey } from './watchLaterCache';
import { loadWatchLaterEntries, watchLaterIndexId, type WatchLaterListResult } from './watchLaterModel';

/**
 * The Watch later feature's React half (2026-08-02): which playlist is appointed, whether a
 * given video is in it, and the two writes — all keyed off the one query that holds the appointed
 * playlist's **complete** entry list (`watchLaterModel.ts` for why complete is the only honest
 * membership source).
 *
 * Every grid card calls this hook; that is fine by construction. The settings read and the session
 * are subscriptions, and the query is one cache entry shared by every caller — 200 cards are 200
 * subscribers, not 200 requests. The settings one is a **slice** rather than the document, so those
 * 200 subscribers also do not re-render when an unrelated setting moves. After a write the cache is **patched in place** rather than
 * invalidated: the `201` already carries the new entry, and a refetch would re-page the whole list
 * to learn one row.
 *
 * The patch itself is **not** here (2026-08-03) — `watchLaterCache.ts` holds it and
 * `useEntryMutations` applies it, because this hook is only one of four controls that change what is
 * in a playlist and the other three left this cache stale.
 */

/** Module-level so the branch is read the same way on every call — `useSettingsSlice` says why. */
const selectWatchLater = (settings: VisiousSettings): WatchLaterChoice | null => settings.watchLater;

/** What a card knows about a video: in the list (removable), out of it, or not yet answerable. */
export type WatchLaterState = 'in' | 'out' | 'unknown';

export interface WatchLaterBinding {
  readonly signedIn: boolean;
  /** The appointed playlist, or `null` — when `null`, every button's first press opens the picker. */
  readonly choice: WatchLaterChoice | null;
  readonly stateOf: (videoId: string) => WatchLaterState;
  readonly busyOf: (videoId: string) => boolean;
  /**
   * Add unless already present — the "if it's not already on there" half of the first-use flow.
   * `playlistId` is passed explicitly because the caller may have appointed it this very tick, one
   * render before the settings subscription catches up.
   */
  readonly add: (videoId: string, playlistId: string) => Promise<boolean>;
  /** Remove the first occurrence. A no-op without a known `indexId`. */
  readonly remove: (videoId: string) => Promise<boolean>;
}

export function useWatchLater(): WatchLaterBinding {
  const session = useSession();
  // `WatchLaterButton` rides every card's corner, so this is per card — the slice, for
  // `useDeArrowBranding`'s reason.
  const choice = useSettingsSlice(selectWatchLater);
  const client = useQueryClient();
  const entries = useEntryMutations();

  const signedIn = session.ready && session.facts.signedIn;
  const grantKey = session.grantedAtEpochSec ?? 0;

  const query = useQuery({
    queryKey: watchLaterQueryKey(choice?.playlistId ?? '', grantKey),
    enabled: signedIn && choice !== null,
    retry: false,
    // The writes patch this cache precisely, so time is not what would make it stale — but another
    // device can append too, and five minutes bounds how long that stays invisible here.
    staleTime: 5 * 60 * 1000,
    queryFn: () => loadList(choice?.playlistId ?? ''),
  });

  // The title snapshot in the settings document corrects itself on every successful read — a
  // playlist renamed elsewhere would otherwise wear its old name in the settings row forever.
  const freshTitle = query.data?.ok === true ? query.data.title : null;
  useEffect(() => {
    if (choice === null || freshTitle === null || freshTitle === '' || freshTitle === choice.title)
      return;
    chooseWatchLater({ playlistId: choice.playlistId, title: freshTitle });
  }, [choice, freshTitle]);

  const list = query.data?.ok === true ? query.data.entries : null;

  // Busy per *video*, this hook's own bookkeeping: the underlying mutation keys an add by playlist,
  // which would grey every card's button for the duration of any one card's write.
  const [writing, setWriting] = useState<ReadonlySet<string>>(NONE_WRITING);
  const track = useCallback(async (videoId: string, work: () => Promise<boolean>) => {
    setWriting((previous) => new Set(previous).add(videoId));
    try {
      return await work();
    } finally {
      setWriting((previous) => {
        const next = new Set(previous);
        next.delete(videoId);
        return next;
      });
    }
  }, []);

  const stateOf = useCallback(
    (videoId: string): WatchLaterState => {
      if (list === null) return 'unknown';
      return watchLaterIndexId(list, videoId) !== null ? 'in' : 'out';
    },
    [list],
  );

  const add = useCallback(
    async (videoId: string, playlistId: string): Promise<boolean> =>
      track(videoId, async () => {
        // "If it's not already on there" — checked against the list itself, which matters
        // most on the first-use path: the playlist was appointed a moment ago and may well already
        // hold the video. `ensureQueryData` reads the cache every ordinary press has already paid for.
        const key = watchLaterQueryKey(playlistId, grantKey);
        const known = await client
          .ensureQueryData<WatchLaterListResult>({ queryKey: key, queryFn: () => loadList(playlistId) })
          .catch((): WatchLaterListResult | null => null);
        if (known?.ok === true && watchLaterIndexId(known.entries, videoId) !== null) return true;

        // The cache patch is `useEntryMutations`' now, not this hook's — see the header.
        const result = await entries.add(playlistId, videoId);
        return result.ok;
      }),
    [client, entries, grantKey, track],
  );

  const remove = useCallback(
    async (videoId: string): Promise<boolean> =>
      track(videoId, async () => {
        if (choice === null || list === null) return false;
        const indexId = watchLaterIndexId(list, videoId);
        if (indexId === null) return false;
        const result = await entries.remove(choice.playlistId, indexId);
        return result.ok;
      }),
    [choice, entries, list, track],
  );

  return useMemo(
    () => ({
      signedIn,
      choice,
      stateOf,
      busyOf: (videoId: string) => writing.has(videoId),
      add,
      remove,
    }),
    [add, choice, remove, signedIn, stateOf, writing],
  );
}

const NONE_WRITING: ReadonlySet<string> = new Set<string>();

async function loadList(playlistId: string): Promise<WatchLaterListResult> {
  const boot = await getPlatform();
  if (!boot.ok) return { ok: false, problem: { kind: 'bad-url', path: '/api/v1/auth/playlists' } };
  return loadWatchLaterEntries(boot.platform, playlistId);
}

/** Appoint (or clear) the Watch later playlist. Plain function — the picker and settings both call it. */
export function chooseWatchLater(choice: WatchLaterChoice | null): void {
  writeSettings({ watchLater: choice });
}

/** Re-exported so the `.tsx` callers can name the type without a core import (R13). */
export type { WatchLaterChoice };
